import React, { useState, useEffect } from "react";
import { Bell, CheckCircle, WarningCircle, Info, X } from "@phosphor-icons/react";
import { api, formatDate } from "../../utils/api";
import { Button } from "../shared/Button";
import { Badge } from "../shared/Badge";
import { Loading, EmptyState } from "../shared/EmptyState";

// Categoría visual + botón de "ir a" según el tipo real que guarda el
// backend en db.notificaciones (ver server.py, sección NOTIFICACIONES y los
// db.notificaciones.insert_one repartidos por el archivo).
const INFO_POR_TIPO = {
  presupuesto: { categoria: "info", tab: "historial" },
  fecha_solicitada: { categoria: "warning", tab: "presupuestos-pendientes" },
  presupuesto_pendiente: { categoria: "warning", tab: "presupuestos-pendientes" },
  presupuesto_aceptado_cliente: { categoria: "success", tab: "presupuestos-pendientes" },
  solicitud: { categoria: "info", tab: "presupuestos-pendientes" },
};

export const Notificaciones = ({ adminToken, onNavigate }) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todas");

  useEffect(() => {
    cargarNotificaciones();
  }, []);

  const cargarNotificaciones = async () => {
    try {
      const data = await api.get("/notificaciones", adminToken);
      setNotificaciones(data);
    } catch (e) {
      console.error("Error cargando notificaciones:", e);
    } finally {
      setLoading(false);
    }
  };

  const marcarLeida = async (id) => {
    setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    try {
      await api.put(`/notificaciones/${id}/leida`, {}, adminToken);
    } catch (e) {
      console.error("Error marcando notificación como leída:", e);
    }
  };

  const eliminar = async (id) => {
    setNotificaciones((prev) => prev.filter((n) => n.id !== id));
    try {
      await api.delete(`/notificaciones/${id}`, adminToken);
    } catch (e) {
      console.error("Error eliminando notificación:", e);
    }
  };

  const categoriaDe = (tipo) => INFO_POR_TIPO[tipo]?.categoria || "info";

  const notificacionesFiltradas = notificaciones.filter((n) => {
    if (filtro === "todas") return true;
    if (filtro === "no-leidas") return !n.leida;
    return categoriaDe(n.tipo) === filtro;
  });

  const getIcono = (tipo) => {
    switch (categoriaDe(tipo)) {
      case "success":
        return <CheckCircle size={24} weight="fill" style={{ color: "var(--success)" }} />;
      case "warning":
        return <WarningCircle size={24} weight="fill" style={{ color: "var(--warning)" }} />;
      case "info":
        return <Info size={24} weight="fill" style={{ color: "var(--info)" }} />;
      default:
        return <Bell size={24} />;
    }
  };

  if (loading) return <Loading />;

  const noLeidas = notificaciones.filter((n) => !n.leida).length;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-4">
          <h1 className="text-h2 font-display mb-1">🔔 Notificaciones</h1>
          <p className="text-small text-secondary">
            {noLeidas > 0 ? `${noLeidas} sin leer` : "Todas leídas"}
          </p>
        </div>

        {/* Filtros */}
        <div className="tabs mb-4">
          <button onClick={() => setFiltro("todas")} className={`tab ${filtro === "todas" ? "tab-active" : ""}`}>
            Todas {notificaciones.length > 0 && `(${notificaciones.length})`}
          </button>
          <button onClick={() => setFiltro("no-leidas")} className={`tab ${filtro === "no-leidas" ? "tab-active" : ""}`}>
            No leídas {noLeidas > 0 && `(${noLeidas})`}
          </button>
          <button onClick={() => setFiltro("warning")} className={`tab ${filtro === "warning" ? "tab-active" : ""}`}>
            ⚠️ Alertas
          </button>
          <button onClick={() => setFiltro("info")} className={`tab ${filtro === "info" ? "tab-active" : ""}`}>
            ℹ️ Info
          </button>
          <button onClick={() => setFiltro("success")} className={`tab ${filtro === "success" ? "tab-active" : ""}`}>
            ✅ Buenas noticias
          </button>
        </div>

        {/* Lista de notificaciones */}
        {notificacionesFiltradas.length === 0 ? (
          <EmptyState
            icon="🔔"
            title="Sin notificaciones"
            description="No hay notificaciones en esta categoría"
          />
        ) : (
          <div className="space-y-3">
            {notificacionesFiltradas.map((notif) => (
              <div
                key={notif.id}
                className={`card p-4 ${!notif.leida ? "border-2" : ""}`}
                style={{
                  borderColor: !notif.leida ? "var(--brand-accent)" : "transparent",
                  opacity: notif.leida ? 0.7 : 1,
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">{getIcono(notif.tipo)}</div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-bold text-small">{notif.titulo}</div>
                      {!notif.leida && (
                        <Badge variant="accent" style={{ fontSize: "0.65rem" }}>
                          NUEVO
                        </Badge>
                      )}
                    </div>
                    <div className="text-small text-secondary mb-2">{notif.mensaje}</div>
                    <div className="text-tiny text-secondary mb-3">
                      {formatDate(notif.created_at)}
                    </div>

                    <div className="flex gap-2">
                      {INFO_POR_TIPO[notif.tipo] && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onNavigate && onNavigate(INFO_POR_TIPO[notif.tipo].tab)}
                        >
                          Ver
                        </Button>
                      )}
                      {!notif.leida && (
                        <Button variant="ghost" size="sm" onClick={() => marcarLeida(notif.id)}>
                          Marcar leída
                        </Button>
                      )}
                      <button
                        onClick={() => eliminar(notif.id)}
                        className="ml-auto text-secondary hover:text-primary"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
